import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { storyAPI } from '../shared/services/api';
import { useAuth } from '../features/auth/contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import StoryCard from '../features/stories/components/StoryCard';
import { Story } from '../shared/types';

const HomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 4rem 0;
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  margin-bottom: 4rem;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: 3rem;
  font-weight: 700;
  color: var(--color-gray-900);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--color-gray-600);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled.button`
  padding: 1rem 2rem;
  background: var(--color-primary);
  color: var(--color-gray-800);
  border: none;
  border-radius: var(--border-radius-lg);
  font-size: 1.1rem;
  font-weight: 600;
  font-family: var(--font-sans);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-md);

  &:hover {
    background: #9bb9db;
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.button`
  padding: 1rem 2rem;
  background: var(--color-white);
  color: var(--color-gray-800);
  border: 2px solid var(--color-primary);
  border-radius: var(--border-radius-lg);
  font-size: 1.1rem;
  font-weight: 600;
  font-family: var(--font-sans);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-md);

  &:hover {
    background: var(--color-primary);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SearchSection = styled.section`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  padding: 2rem;
  margin-bottom: 3rem;
`;

const SearchTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 2rem;
  font-weight: 600;
  color: var(--color-gray-900);
  text-align: center;
  margin-bottom: 1.5rem;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: var(--font-sans);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const SearchSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: var(--font-sans);
  background: var(--color-white);
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const SearchButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--color-primary);
  color: var(--color-gray-800);
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-sans);
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #8BB5E0;
  }
`;

const StoriesSection = styled.section`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 2rem;
  font-weight: 600;
  color: var(--color-gray-900);
  text-align: center;
  margin-bottom: 2rem;
`;

const StoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--color-gray-500);
`;

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, login, user, initialized } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [topStories, setTopStories] = useState<Story[]>([]);
  const [searchResults, setSearchResults] = useState<Story[]>([]);
  const [genres, setGenres] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Check for admin redirect on mount
  useEffect(() => {
    if (initialized && isAuthenticated && user) {
      const savedPath = sessionStorage.getItem('adminRedirectPath');
      if (savedPath && savedPath.startsWith('/admin')) {
        // Check if user is admin
        const isAdmin = user.roles?.some(role =>
          ['ADMIN', 'SUPER_ADMIN', 'MODERATOR', 'ANALYST'].includes(role.toUpperCase())
        ) || user.username === 'admin' || user.username === 'alex';

        if (isAdmin) {
          sessionStorage.removeItem('adminRedirectPath');
          navigate(savedPath, { replace: true });
        }
      }
    }
  }, [initialized, isAuthenticated, user, navigate]);

  // Initialize from URL params on mount
  useEffect(() => {
    const query = searchParams.get('q') || '';
    const genre = searchParams.get('genre') || '';
    if (query || genre) {
      setSearchQuery(query);
      setSelectedGenre(genre);
      setHasSearched(true);
      performSearch(query, genre);
    } else {
      loadInitialData();
    }
  }, []);

  // Re-search when URL params change
  useEffect(() => {
    const query = searchParams.get('q') || '';
    const genre = searchParams.get('genre') || '';

    // Only search if params actually changed
    if (query !== searchQuery || genre !== selectedGenre) {
      setSearchQuery(query);
      setSelectedGenre(genre);
      if (query || genre) {
        setHasSearched(true);
        performSearch(query, genre);
      } else if (hasSearched) {
        // Clear search results
        setSearchResults([]);
        setHasSearched(false);
      }
    }
  }, [searchParams]);

  const loadInitialData = async () => {
    try {
      const [topStoriesRes, genresRes] = await Promise.all([
        storyAPI.getTopPublicStories(15),
        storyAPI.getGenres(),
      ]);
      
      setTopStories(topStoriesRes.data);
      setGenres(genresRes.data);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async (query: string, genre: string) => {
    setSearchLoading(true);
    try {
      const params: any = {};
      if (query) params.q = query;
      if (genre) params.genre = genre;

      const response = await storyAPI.searchPublicStories(params);
      setSearchResults(response.data.stories);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSearched(true);

    // Update URL params to trigger search via useEffect
    const newParams = new URLSearchParams();
    if (searchQuery) newParams.set('q', searchQuery);
    if (selectedGenre) newParams.set('genre', selectedGenre);
    setSearchParams(newParams);
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      window.location.href = '/dashboard';
    } else {
      login();
    }
  };

  const handleCreateStory = () => {
    if (isAuthenticated) {
      window.location.href = '/story/new';
    } else {
      login();
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const displayStories = hasSearched ? searchResults : topStories;
  const displayTitle = hasSearched ? t('home.searchResults') : t('home.topRatedStories');

  return (
    <HomeContainer>
      <HeroSection>
        <HeroTitle>{t('home.heroTitle')}</HeroTitle>
        <HeroSubtitle>
          {t('home.heroSubtitle')}
        </HeroSubtitle>
        <HeroButtons>
          <PrimaryButton onClick={handleGetStarted}>
            {t('home.getStarted')}
          </PrimaryButton>
          <SecondaryButton onClick={handleCreateStory}>
            {t('home.createStory')}
          </SecondaryButton>
        </HeroButtons>
      </HeroSection>

      <SearchSection>
        <SearchTitle>{t('home.discoverStories')}</SearchTitle>
        <SearchForm onSubmit={handleSearch}>
          <SearchInput
            type="text"
            placeholder={t('home.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <SearchSelect
            value={selectedGenre}
            onChange={(e) => setSelectedGenre(e.target.value)}
          >
            <option value="">{t('home.allGenres')}</option>
            {genres.map((genre) => (
              <option key={genre} value={genre}>
                {t(`genres.${genre}`, genre)}
              </option>
            ))}
          </SearchSelect>
          <SearchButton type="submit" disabled={searchLoading}>
            {searchLoading ? t('home.searching') : t('home.search')}
          </SearchButton>
        </SearchForm>
      </SearchSection>

      <StoriesSection>
        <SectionTitle>{displayTitle}</SectionTitle>
        
        {searchLoading ? (
          <EmptyState>Searching stories...</EmptyState>
        ) : displayStories.length > 0 ? (
          <StoriesGrid>
            {displayStories.map((story) => (
              <StoryCard key={story.id} story={story} showAuthor={true} />
            ))}
          </StoriesGrid>
        ) : (
          <EmptyState>
            {hasSearched ? t('home.noStoriesFound') : t('home.noStoriesAvailable')}
          </EmptyState>
        )}
      </StoriesSection>
    </HomeContainer>
  );
};

export default HomePage;